/* Piex help windows. Native <dialog> + showModal() gives inert background and Escape.
   Opened by [data-piex-help="key"] or any link whose hash is #ayuda-<key>, and by that hash in the URL.
   Entry points of a hidden window are hidden too, so no dead link remains.
   #ayuda-cambios opens the shipping window scrolled to its returns heading. */
const PIEX_HELP_ALIASES = {cambios: 'envios'};
if (!customElements.get('piex-help-modals')) {
  customElements.define('piex-help-modals', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.dialogs = {};
      this.querySelectorAll('dialog[data-piex-help-dialog]').forEach(dialog => {
        this.dialogs[dialog.dataset.piexHelpDialog] = dialog;
        dialog.addEventListener('click', event => this.onDialogClick(event, dialog), options);
        dialog.addEventListener('keydown', event => this.trapTab(event, dialog), options);
        dialog.addEventListener('close', () => this.onClose(dialog), options);
        const shippingDetails = dialog.querySelector('.phv-shipping-conditions');
        shippingDetails?.addEventListener('toggle', () => {
          dialog.querySelectorAll('[data-piex-shipping-details]').forEach(button => button.setAttribute('aria-expanded', String(shippingDetails.open)));
        }, options);
        const lead = dialog.querySelector('.piex-help__content p, .piex-help__content li');
        if (lead) {
          lead.id = lead.id || `${dialog.id}-lead`;
          dialog.setAttribute('aria-describedby', lead.id);
        }
      });
      this.hideOrphanEntries();
      document.addEventListener('click', event => this.onDocumentClick(event), options);
      window.addEventListener('hashchange', () => this.openFromHash(), options);
      document.addEventListener('shopify:block:select', event => {
        const dialog = event.target.closest?.('dialog[data-piex-help-dialog]');
        if (dialog) this.open(dialog.dataset.piexHelpDialog, null);
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        const dialog = event.target.closest?.('dialog[data-piex-help-dialog]');
        if (dialog?.open) dialog.close();
      }, options);
      this.openFromHash();
    }

    disconnectedCallback() {
      Object.values(this.dialogs || {}).forEach(dialog => dialog.open && dialog.close());
      this.abort?.abort();
    }

    keyOf(element) {
      if (element.dataset.piexHelp) return element.dataset.piexHelp;
      const match = element.tagName === 'A' && /^#ayuda-([a-z]+)$/.exec(element.hash || '');
      return match ? match[1] : null;
    }

    dialogFor(key) {
      return this.dialogs[PIEX_HELP_ALIASES[key] || key];
    }

    hideOrphanEntries() {
      // Undo a previous pass first: the theme editor re-renders this section when a window is switched back on.
      document.querySelectorAll('[data-piex-help-hidden]').forEach(element => {
        element.hidden = false;
        delete element.dataset.piexHelpHidden;
      });
      document.querySelectorAll('[data-piex-help], a[href*="#ayuda-"]').forEach(element => {
        const key = this.keyOf(element);
        if (!key || this.dialogFor(key)) return;
        if (/^H[1-6]$/.test(element.parentElement?.tagName)) {
          element.removeAttribute('href');
          return;
        }
        const wrapper = element.closest('[data-piex-help-entry], li, .section-text-with-icons-type-1__button') || element;
        wrapper.hidden = true;
        wrapper.dataset.piexHelpHidden = '';
      });
    }

    onDocumentClick(event) {
      const trigger = event.target.closest('[data-piex-help], a[href*="#ayuda-"]');
      if (!trigger) return;
      const key = this.keyOf(trigger);
      if (!key || !this.dialogFor(key)) return;
      event.preventDefault();
      const fromDialog = trigger.closest('dialog[data-piex-help-dialog]');
      this.open(key, fromDialog ? this.opener : trigger);
    }

    open(key, opener) {
      const dialog = this.dialogFor(key);
      if (!dialog) return;
      Object.values(this.dialogs).forEach(other => {
        if (other !== dialog && other.open) {
          other.dataset.piexHelpSwitching = 'true';
          other.close();
        }
      });
      this.opener = opener;
      if (!dialog.open) dialog.showModal();
      dialog.querySelectorAll('image[data-piex-href]').forEach(image => {
        image.setAttribute('href', image.dataset.piexHref);
        image.removeAttribute('data-piex-href');
      });
      this.loadMap(dialog);
      document.documentElement.classList.add('piex-help-open');
      const body = dialog.querySelector('.piex-help__body');
      const section = PIEX_HELP_ALIASES[key] && dialog.querySelector(`[data-piex-help-section="${key}"]`);
      if (section?.tagName === 'DETAILS') section.open = true;
      if (body) body.scrollTop = section ? section.offsetTop - body.offsetTop - 8 : 0;
    }

    loadMap(dialog) {
      const holder = dialog.querySelector('[data-piex-help-map]');
      if (!holder || holder.querySelector('iframe')) return;
      const iframe = document.createElement('iframe');
      iframe.src = holder.dataset.piexHelpMap;
      iframe.title = holder.dataset.piexHelpMapTitle || '';
      // The dialog is already visible. Lazy loading could leave its map blank.
      iframe.loading = 'eager';
      iframe.tabIndex = -1; // keyboard focus inside the map would swallow Escape and Tab; "Como llegar" covers it
      iframe.referrerPolicy = 'no-referrer-when-downgrade';
      iframe.setAttribute('allowfullscreen', '');
      holder.appendChild(iframe);
    }

    onDialogClick(event, dialog) {
      const shippingTrigger = event.target.closest('[data-piex-shipping-details]');
      if (shippingTrigger) {
        const details = dialog.querySelector('.phv-shipping-conditions');
        if (details) {
          details.hidden = false;
          details.open = true;
          dialog.querySelectorAll('[data-piex-shipping-details]').forEach(button => button.setAttribute('aria-expanded', 'true'));
          details.querySelector('summary')?.focus();
        }
        return;
      }
      if (event.target.closest('[data-piex-help-close]')) {
        dialog.close();
        return;
      }
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    }

    trapTab(event, dialog) {
      if (event.key !== 'Tab') return;
      const focusable = [...dialog.querySelectorAll('a[href], summary, button:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter(element => element.offsetParent !== null || element === document.activeElement);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onClose(dialog) {
      if (dialog.dataset.piexHelpSwitching) {
        delete dialog.dataset.piexHelpSwitching;
        return;
      }
      document.documentElement.classList.remove('piex-help-open');
      if (/^#ayuda-/.test(location.hash)) history.replaceState(null, '', location.pathname + location.search);
      if (this.opener?.isConnected) this.opener.focus({preventScroll: true});
      this.opener = null;
    }

    openFromHash() {
      const match = /^#ayuda-([a-z]+)$/.exec(location.hash);
      if (match && this.dialogFor(match[1])) this.open(match[1], null);
    }
  });
}
